// Natural disasters. The host decides when one happens and with what parameters; every client runs the
// same deterministic timeline from those parameters and applies pushes/damage to the cars it owns.
// Disaster damage never takes a car below 1 HP (Match.disasterDamage).

import { Rng } from './rng';
import type { Match, PlayerState } from './match';
import type { Ground } from './ground';

export type DisasterKind = 'tsunami' | 'earthquake' | 'volcano' | 'cyclone';

export interface DisasterSpec {
  id: number;
  kind: DisasterKind;
  /** match time when the warning banner starts */
  start: number;
  /** warning length in seconds */
  warn: number;
  /** active duration after the warning */
  duration: number;
  seed: number;
  /** kind-specific parameters (all deterministic from seed on the host) */
  p: Record<string, number>;
}

export interface Impactor { id: number; x: number; z: number; landAt: number; radius: number; kind: 'debris' | 'rock'; fireUntil?: number; }

export const DISASTER = {
  firstAt: 90, minGap: 75, maxGap: 120, noneInLast: 30, warn: 5,
  tsunami: { speed: 14, thickness: 12, height: 3.5, damage: 30, push: 24 },
  earthquake: { duration: 20, debrisEvery: 1.2, debrisDamage: 10, debrisRadius: 4.5, fall: 2.0, wobble: 0.35 },
  volcano: { duration: 25, rockEvery: 0.7, rockDamage: 15, rockRadius: 5, fall: 2.5, fireSeconds: 8, fireDps: 2, fireRadius: 3.2 },
  cyclone: { duration: 26, speed: 4, pullRadius: 25, pull: 11, flingRadius: 6, flingDamage: 20, controlLoss: 2 },
} as const;

const NAMES: Record<DisasterKind, { big: string; from: string; tip: string }> = {
  tsunami: { big: 'TSUNAMI!', from: 'Coming from the bay', tip: 'Hide behind a building on its inland side, or get to high ground!' },
  earthquake: { big: 'EARTHQUAKE!', from: 'The ground is shaking', tip: 'Stay clear of tall buildings and the falling debris shadows.' },
  volcano: { big: 'VOLCANO!', from: 'The mountain is erupting', tip: 'Watch for the target circles and keep moving.' },
  cyclone: { big: 'CYCLONE!', from: 'A tornado is on the loose', tip: 'Drive away from it. Buildings make good shields.' },
};
export function disasterCopy(kind: DisasterKind): { big: string; from: string; tip: string } { return NAMES[kind]; }

interface Active {
  spec: DisasterSpec;
  rng: Rng;
  hit: Set<string>;         // player ids already hit (tsunami / cyclone fling)
  impactors: Impactor[];
  nextImpactAt: number;
  nextImpactId: number;
  // cyclone path
  cx: number; cz: number; dx: number; dz: number;
}

export class DisasterDirector {
  readonly active: Active[] = [];
  readonly sheltered = new Set<string>();
  private nextAt: number;
  private nextId = 1;
  /** per-player control loss from cyclone flings */
  readonly noControlUntil = new Map<string, number>();
  private scheduleRng: Rng;
  constructor(readonly bounds: { x: [number, number]; z: [number, number] }, readonly ground: Ground, readonly volcano: [number, number, number], seed: number, readonly durationSec: number) {
    this.scheduleRng = new Rng(seed ^ 0x5eed);
    this.nextAt = DISASTER.firstAt;
  }

  /** Host only: decide if a new disaster starts now. Returns the spec to broadcast, or null. */
  maybeSchedule(time: number): DisasterSpec | null {
    if (time < this.nextAt) return null;
    if (time > this.durationSec - DISASTER.noneInLast - 30) { this.nextAt = Infinity; return null; }
    const kinds: DisasterKind[] = ['tsunami', 'earthquake', 'volcano', 'cyclone'];
    // avoid repeating the previous kind
    const last = this.active.at(-1)?.spec.kind;
    const pool = kinds.filter((k) => k !== last);
    const kind = pool[this.scheduleRng.int(pool.length)];
    const spec = this.makeSpec(kind, time, this.scheduleRng.int(1e9));
    this.nextAt = time + DISASTER.warn + spec.duration + this.scheduleRng.range(DISASTER.minGap, DISASTER.maxGap) - spec.duration;
    return spec;
  }

  makeSpec(kind: DisasterKind, time: number, seed: number): DisasterSpec {
    const r = new Rng(seed);
    const b = this.bounds;
    const p: Record<string, number> = {};
    let duration = 20;
    if (kind === 'tsunami') { const w = b.x[1] - b.x[0] + 80; duration = w / DISASTER.tsunami.speed; p.x0 = b.x[0] - 40; }
    else if (kind === 'earthquake') duration = DISASTER.earthquake.duration;
    else if (kind === 'volcano') duration = DISASTER.volcano.duration;
    else if (kind === 'cyclone') {
      duration = DISASTER.cyclone.duration;
      // start on a random edge, head across the map
      const side = r.int(4);
      const cx = side === 0 ? b.x[0] : side === 1 ? b.x[1] : r.range(b.x[0], b.x[1]);
      const cz = side === 2 ? b.z[0] : side === 3 ? b.z[1] : r.range(b.z[0], b.z[1]);
      const tx = r.range(b.x[0] + 60, b.x[1] - 60), tz = r.range(b.z[0] + 60, b.z[1] - 60);
      const d = Math.hypot(tx - cx, tz - cz) || 1;
      p.cx = cx; p.cz = cz; p.dx = (tx - cx) / d; p.dz = (tz - cz) / d;
    }
    return { id: this.nextId++, kind, start: time, warn: DISASTER.warn, duration, seed, p };
  }

  /** Everyone: begin running a disaster from its spec (from the host's event or snapshot). */
  begin(spec: DisasterSpec): void {
    if (this.active.some((a) => a.spec.id === spec.id)) return;
    this.nextId = Math.max(this.nextId, spec.id + 1);
    this.active.push({ spec, rng: new Rng(spec.seed), hit: new Set(), impactors: [], nextImpactAt: spec.start + spec.warn, nextImpactId: 1, cx: spec.p.cx ?? 0, cz: spec.p.cz ?? 0, dx: spec.p.dx ?? 0, dz: spec.p.dz ?? 0 });
  }

  current(): Active | null { return this.active.find((a) => a.spec.start + a.spec.warn + a.spec.duration > 0) ?? null; }
  activeSpecs(time: number): DisasterSpec[] { return this.active.filter((a) => time < a.spec.start + a.spec.warn + a.spec.duration).map((a) => a.spec); }

  /** Position of the tsunami front / cyclone centre at a time, for visuals. */
  tsunamiFront(a: Active, time: number): number { return a.spec.p.x0 + Math.max(0, time - a.spec.start - a.spec.warn) * DISASTER.tsunami.speed; }
  cyclonePos(a: Active, time: number): { x: number; z: number } {
    const t = Math.max(0, time - a.spec.start - a.spec.warn);
    return { x: a.cx + a.dx * t * DISASTER.cyclone.speed + Math.sin(t * 0.7) * 18, z: a.cz + a.dz * t * DISASTER.cyclone.speed + Math.cos(t * 0.9) * 18 };
  }

  /** Nearby active danger for local passenger chatter; warnings alone do not count.
   * Earthquake shaking is map-wide. Rocks and fire remain dangerous after an eruption ends.
   */
  nearbyDanger(x: number, z: number, time: number): DisasterKind | null {
    let quake = false;
    for (const a of this.active) {
      const start = a.spec.start + a.spec.warn, end = start + a.spec.duration;
      if (time < start) continue;
      switch (a.spec.kind) {
        case 'tsunami':
          if (time <= end && z >= this.bounds.z[0] - 20 && z <= this.bounds.z[1] + 20 &&
            Math.abs(x - this.tsunamiFront(a, time)) <= 45) return 'tsunami';
          break;
        case 'cyclone': {
          const c = this.cyclonePos(a, time);
          if (time <= end && Math.hypot(x - c.x, z - c.z) <= DISASTER.cyclone.pullRadius + 20) return 'cyclone';
          break;
        }
        case 'volcano':
          if (time <= end && Math.hypot(x - this.volcano[0], z - this.volcano[2]) <= 100) return 'volcano';
          if (a.impactors.some(im => time <= (im.fireUntil ?? im.landAt) &&
            Math.hypot(x - im.x, z - im.z) <= im.radius + 20)) return 'volcano';
          break;
        case 'earthquake':
          if (time <= end || a.impactors.some(im => time <= im.landAt && Math.hypot(x - im.x, z - im.z) <= im.radius + 20)) quake = true;
          break;
      }
    }
    return quake ? 'earthquake' : null;
  }

  /** Steering wobble to add to owned cars' input during an earthquake. */
  steerWobble(time: number): number {
    for (const a of this.active) if (a.spec.kind === 'earthquake' && time > a.spec.start + a.spec.warn && time < a.spec.start + a.spec.warn + a.spec.duration) return Math.sin(time * 9) * DISASTER.earthquake.wobble * Math.sin(time * 2.3);
    return 0;
  }

  /** Advance all disasters; apply effects to the owned players. */
  step(match: Match, owned: PlayerState[], dt: number): void {
    const time = match.time;
    this.sheltered.clear();
    for (const a of this.active) {
      const { spec } = a; const t0 = spec.start + spec.warn; const t1 = t0 + spec.duration;
      if (time < t0) continue;
      if (time > t1 + 10) continue; // keep for a while so visuals can fade
      if (spec.kind === 'tsunami') {
        if (time > t1) continue;
        const front = this.tsunamiFront(a, time);
        for (const p of owned) {
          if (a.hit.has(p.id) || p.respawnAt != null) continue;
          const dx = p.car.x - front;
          if (this.ground.shelteredFrom(p.car.x, p.car.z, p.car.x - 24, p.car.z, p.car.y)) {
            this.sheltered.add(p.id); continue;
          }
          if (dx > -DISASTER.tsunami.thickness && dx < 4 && p.car.y < DISASTER.tsunami.height) {
            a.hit.add(p.id);
            match.disasterDamage(p, DISASTER.tsunami.damage);
            p.car.vx += DISASTER.tsunami.push; p.car.vy = 5; p.car.airborne = true;
          }
        }
      } else if (spec.kind === 'earthquake' || spec.kind === 'volcano') {
        const isQuake = spec.kind === 'earthquake';
        const every = isQuake ? DISASTER.earthquake.debrisEvery : DISASTER.volcano.rockEvery;
        const fall = isQuake ? DISASTER.earthquake.fall : DISASTER.volcano.fall;
        while (a.nextImpactAt <= time && a.nextImpactAt < t1) {
          const pos = isQuake ? this.debrisSpot(a.rng) : this.rockSpot(a.rng);
          a.impactors.push({ id: a.nextImpactId++, x: pos.x, z: pos.z, landAt: a.nextImpactAt + fall, radius: isQuake ? DISASTER.earthquake.debrisRadius : DISASTER.volcano.rockRadius, kind: isQuake ? 'debris' : 'rock' });
          a.nextImpactAt += every;
        }
        for (const im of a.impactors) {
          if (im.landAt <= time && im.landAt > time - dt) {
            for (const p of owned) if (p.respawnAt == null && Math.hypot(p.car.x - im.x, p.car.z - im.z) < im.radius + 1 && !this.ground.shelteredFrom(p.car.x, p.car.z, im.x, im.z, p.car.y)) {
              match.disasterDamage(p, isQuake ? DISASTER.earthquake.debrisDamage : DISASTER.volcano.rockDamage);
              const d = Math.hypot(p.car.x - im.x, p.car.z - im.z) || 1; p.car.vx += (p.car.x - im.x) / d * 8; p.car.vz += (p.car.z - im.z) / d * 8;
            }
            if (!isQuake) im.fireUntil = im.landAt + DISASTER.volcano.fireSeconds;
          }
          if (im.fireUntil && time < im.fireUntil && time > im.landAt) {
            for (const p of owned) if (p.respawnAt == null && Math.hypot(p.car.x - im.x, p.car.z - im.z) < DISASTER.volcano.fireRadius && !this.ground.shelteredFrom(p.car.x, p.car.z, im.x, im.z, p.car.y)) {
              p.fireTick = (p.fireTick ?? 0) + dt;
              if (p.fireTick >= 1) { p.fireTick -= 1; match.disasterDamage(p, DISASTER.volcano.fireDps); }
            }
          }
        }
        if (a.impactors.length > 60) a.impactors.splice(0, a.impactors.length - 60);
      } else if (spec.kind === 'cyclone') {
        if (time > t1) continue;
        const c = this.cyclonePos(a, time);
        for (const p of owned) {
          if (p.respawnAt != null) continue;
          const dx = c.x - p.car.x, dz = c.z - p.car.z; const d = Math.hypot(dx, dz);
          if (d < DISASTER.cyclone.pullRadius && this.ground.shelteredFrom(p.car.x, p.car.z, c.x, c.z, p.car.y)) {
            this.sheltered.add(p.id); continue;
          }
          if (d < DISASTER.cyclone.pullRadius && d > 0.1) {
            const k = (1 - d / DISASTER.cyclone.pullRadius) * DISASTER.cyclone.pull * dt;
            p.car.vx += dx / d * k; p.car.vz += dz / d * k;
          }
          if (d < DISASTER.cyclone.flingRadius && !a.hit.has(p.id)) {
            a.hit.add(p.id);
            match.disasterDamage(p, DISASTER.cyclone.flingDamage);
            const ang = a.rng.next() * Math.PI * 2;
            p.car.vx = Math.cos(ang) * 20; p.car.vz = Math.sin(ang) * 20; p.car.vy = 12; p.car.airborne = true;
            this.noControlUntil.set(p.id, time + DISASTER.cyclone.controlLoss);
          }
          if (d > DISASTER.cyclone.flingRadius * 3) a.hit.delete(p.id);
        }
      }
    }
    // drop long-finished disasters
    for (let i = this.active.length - 1; i >= 0; i--) { const a = this.active[i]; if (time > a.spec.start + a.spec.warn + a.spec.duration + 12) this.active.splice(i, 1); }
  }

  private debrisSpot(rng: Rng): { x: number; z: number } {
    // near tall buildings, on drivable ground
    // anywhere open that sits within ~12 m of something over 18 m tall
    for (let i = 0; i < 40; i++) {
      const x = rng.range(this.bounds.x[0], this.bounds.x[1]), z = rng.range(this.bounds.z[0], this.bounds.z[1]);
      if (this.ground.blockedAt(x, z) || this.ground.surfaceAt(x, z) === 0) continue;
      let tall = false;
      for (let k = 0; k < 8 && !tall; k++) { const a = k / 8 * Math.PI * 2; tall = this.ground.topAt(x + Math.cos(a) * 9, z + Math.sin(a) * 9) > 18; }
      if (tall) return { x, z };
    }
    return { x: rng.range(this.bounds.x[0], this.bounds.x[1]), z: rng.range(this.bounds.z[0], this.bounds.z[1]) };
  }
  private rockSpot(rng: Rng): { x: number; z: number } {
    // denser toward the volcano corner
    for (let i = 0; i < 30; i++) {
      const x = rng.range(this.bounds.x[0], this.bounds.x[1]), z = rng.range(this.bounds.z[0], this.bounds.z[1]);
      const d = Math.hypot(x - this.volcano[0], z - this.volcano[2]);
      const accept = Math.max(0.15, 1 - d / 900);
      if (rng.next() < accept && this.ground.surfaceAt(x, z) !== 0) return { x, z };
    }
    return { x: rng.range(this.bounds.x[0], this.bounds.x[1]), z: rng.range(this.bounds.z[0], this.bounds.z[1]) };
  }
  private inside(x: number, z: number): boolean { return x > this.bounds.x[0] && x < this.bounds.x[1] && z > this.bounds.z[0] && z < this.bounds.z[1]; }
}
